import { useEffect } from "react";
import {
  NOT_APPLICABLE,
  PMO_SELF_ASSESSMENT_REPORT_TEMPLATE as T,
  UNABLE_TO_ASSESS,
  type PmoAreaResult,
  type PmoReportModel,
} from "../lib/pmoReport";
import { percentOfScale, shareOfScale } from "../lib/share";
import { AreaIcon } from "./AreaIcon";
import { HarveyBall } from "./HarveyBall";

// The customer's report — decision §5 as amended by the Business Architect's
// C1–C6 ruling, rendered through the pure model in pmoReport.ts. This
// component adds layout and nothing else: every number and every sentence of
// interpretation comes from the model or the template.
//
// Printing is the MVP's PDF (decision §5; dashboard proposal V1.1 §4,
// "Download PDF report"): the existing print stylesheet strips the chrome.
// `autoPrint` opens the browser's print dialog once the report has rendered,
// which is how the owner's "Download PDF report" works.

const fmt = (n: number) => n.toFixed(2);

export function PmoClientReport({
  model: m,
  scale,
  autoPrint = false,
}: {
  model: PmoReportModel;
  scale: { min: number; max: number };
  autoPrint?: boolean;
}) {
  useEffect(() => {
    if (!autoPrint) return;
    const t = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(t);
  }, [autoPrint]);

  const date = new Date(m.identity.assessedOn).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <article className="report">
      {!T.disclosureApproved && <div className="report-watermark">{T.previewLabel}</div>}

      <div className="no-print" style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.5rem" }}>
        <button className="btn btn-secondary" onClick={() => window.print()}>Print or save as PDF</button>
      </div>

      <h1 style={{ marginBottom: "0.25rem" }}>PMO Self-Assessment Report</h1>
      <p style={{ marginTop: 0, color: "var(--text-muted)" }}>
        {m.identity.customer} · completed {date}
      </p>

      {/* The first screen leads with the result (journey slice 4): the overall
          level, its coverage (A8) and the three focus areas by name. */}
      <section className="card">
        <h2>Your result</h2>
        {m.overall.average !== null ? (
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", margin: "0.25rem 0" }}>
            <HarveyBall
              share={shareOfScale(m.overall.average, scale)}
              size={72}
              label={`${percentOfScale(m.overall.average, scale)} % in place`}
            />
            <div>
              <p style={{ fontSize: "1.5rem", fontWeight: 700, margin: 0 }}>{m.overall.state}</p>
              <p style={{ margin: 0, color: "var(--text-muted)" }}>
                {percentOfScale(m.overall.average, scale)} % in place — average {fmt(m.overall.average)} on the {scale.min}–{scale.max} answer scale
              </p>
            </div>
          </div>
        ) : (
          <p>{m.summary}</p>
        )}
        <p>
          <strong>Coverage: {m.overall.coverageText}</strong>
        </p>
        {m.overall.isPartial && (
          <p className="report-stale">
            <strong>{T.partialLabel}</strong>
          </p>
        )}
        {m.focusAreas.length > 0 && (
          <>
            <p style={{ marginBottom: "0.25rem" }}>Where to look first:</p>
            <ol style={{ marginTop: 0 }}>
              {m.focusAreas.map((f) => (
                <li key={f.blockId}>
                  <AreaIcon blockId={f.blockId} /> <strong>{f.title}</strong> — {f.state}
                </li>
              ))}
            </ol>
          </>
        )}
      </section>

      <section className="card">
        <h2>{T.focusHeading}</h2>
        <p>{T.focusExplanation}</p>
        {m.focusAreas.length === 0 && <p>{m.focusNote}</p>}
        <ol>
          {m.focusAreas.map((f) => (
            <li key={f.blockId} style={{ marginBottom: "0.75rem" }}>
              <AreaIcon blockId={f.blockId} /> <strong>{f.title}</strong> — {f.state}
              <ul>
                {f.opportunities.map((o, i) => (
                  <li key={i}>
                    {o.text}
                    {o.reason && <span style={{ color: "var(--text-secondary)" }}> — Your reason: {o.reason}</span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        {m.focusAreas.length > 0 && m.focusNote && <p>{m.focusNote}</p>}
        <p style={{ fontSize: "var(--lf-body-size)", color: "var(--text-muted)" }}>{T.focusRule}</p>
      </section>

      <section className="card">
        <h2>Your PMO profile — self-declared implementation levels</h2>
        <p style={{ marginTop: 0, color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Each circle shows how far the practice area is in place, from empty (not in place) to full (fully in place),
          as the share of the answer scale the area's average represents.
        </p>
        <div className="report-glance">
          {m.areas.map((a) => (
            <AreaGlance key={a.blockId} area={a} scale={scale} />
          ))}
        </div>
        {m.areas.map((a) => (
          <AreaBlock key={a.blockId} area={a} scale={scale} />
        ))}
      </section>

      <section className="card">
        <h2>Improvement opportunities</h2>
        {m.opportunities.length === 0 ? (
          <p>No practice was answered as not in place or partly in place.</p>
        ) : (
          <table className="report-table">
            <thead>
              <tr><th>Practice area</th><th>Practice</th><th>Your answer</th><th>Opportunity</th><th>Your reason</th></tr>
            </thead>
            <tbody>
              {m.opportunities.map((o, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <AreaIcon blockId={o.blockId} /> {o.areaTitle}
                  </td>
                  <td>{o.perspective}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <HarveyBall share={shareOfScale(o.score, scale)} size={16} label={o.answer} /> {o.answer}
                  </td>
                  <td>{o.text}</td>
                  <td>{o.reason ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h2>About this assessment</h2>
        <dl className="report-grid">
          <dt>Organisation</dt><dd>{m.identity.customer}</dd>
          <dt>Completed</dt><dd>{date}</dd>
          <dt>Respondent</dt><dd>{T.respondentStatement}</dd>
          <dt>Instrument</dt><dd>{m.identity.instrumentTitle}, version {m.identity.instrumentVersion}</dd>
          <dt>Method</dt><dd>{T.methodStatement} {T.levelsStatement}</dd>
        </dl>
        <p>{m.summary}</p>
        {m.overall.average !== null && (
          <p>
            <strong>Overall self-declared implementation level: {m.overall.state}</strong>{" "}
            <span style={{ color: "var(--text-muted)" }}>
              (average {fmt(m.overall.average)} on the {scale.min}–{scale.max} answer scale, across {m.overall.areasComplete} fully
              assessed {m.overall.areasComplete === 1 ? "area" : "areas"})
            </span>
            {/* A8 (disclosure policy V1.6, F5): the coverage figure beside
                this printing of the overall too, not only in "Your result". */}
            {" · "}
            <strong>Coverage: {m.overall.coverageText}</strong>
          </p>
        )}
        {m.overall.average !== null && m.overall.isPartial && (
          <p className="report-stale">
            <strong>{T.partialLabel}</strong>
          </p>
        )}
        {!m.overall.isComplete && (
          <p className="report-stale">
            This assessment is incomplete: {m.overall.areasIncomplete} practice{" "}
            {m.overall.areasIncomplete === 1 ? "area has" : "areas have"} questions that could not be assessed. The
            overall indication rests only on the fully assessed areas.
          </p>
        )}
        <p style={{ fontSize: "var(--lf-body-size)", color: "var(--text-muted)" }}>{T.coverageStatement}</p>
      </section>

      <section className="card">
        <h2>Limitations</h2>
        <p>{T.limitationsStatement}</p>
        <p>{T.criticalStatement}</p>
      </section>

      <footer className="report-footer">
        {T.id} {T.version} · instrument {m.identity.instrumentVersion} · {m.overall.derivation} ·{" "}
        <span style={{ fontFamily: "var(--lf-font-mono)", overflowWrap: "anywhere" }}>{m.identity.definitionHash}</span>
      </footer>
    </article>
  );
}

function ballFor(a: PmoAreaResult, scale: { min: number; max: number }) {
  if (a.status === "complete") {
    const pct = percentOfScale(a.average, scale);
    return { share: shareOfScale(a.average, scale), state: "share" as const, label: `${pct} % in place`, text: `${pct} % in place` };
  }
  if (a.status === "incomplete") {
    return { share: null, state: "incomplete" as const, label: "Incomplete — not classified", text: `Incomplete — ${a.assessed} of ${a.applicable} assessed` };
  }
  return { share: null, state: "not_applicable" as const, label: "Not applicable", text: "Not applicable" };
}

function AreaGlance({ area: a, scale }: { area: PmoAreaResult; scale: { min: number; max: number } }) {
  const ball = ballFor(a, scale);
  return (
    <div className="report-glance-item">
      <HarveyBall share={ball.share} state={ball.state} size={40} label={ball.label} />
      <div>
        <div style={{ fontWeight: 600, fontSize: "var(--lf-body-size)", display: "flex", alignItems: "center", gap: "0.35rem" }}>
          <AreaIcon blockId={a.blockId} size={16} /> <span>{a.sequence}. {a.title}</span>
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
          {a.status === "complete" ? `${ball.text} · ${a.state}` : ball.text}
        </div>
      </div>
    </div>
  );
}

function AreaBlock({ area: a, scale }: { area: PmoAreaResult; scale: { min: number; max: number } }) {
  const ball = ballFor(a, scale);
  const heading =
    a.status === "complete"
      ? `${ball.text} · ${a.state}`
      : a.status === "incomplete"
        ? `Incomplete — ${a.assessed} of ${a.applicable} applicable ${a.applicable === 1 ? "question" : "questions"} assessed; not classified`
        : "Not applicable";
  return (
    <div style={{ borderTop: "1px solid var(--border)", padding: "0.6rem 0" }}>
      <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <HarveyBall share={ball.share} state={ball.state} size={24} label={ball.label} />
        <span>{a.sequence}. {a.title}</span>
        <span style={{ fontWeight: 400 }}>· {heading}</span>
        {a.status === "complete" && (
          <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
            (average {fmt(a.average!)} of {scale.max})
          </span>
        )}
      </h3>
      {a.status === "not_applicable" && a.justification && (
        <p style={{ margin: "0.3rem 0", color: "var(--text-secondary)" }}>Reason given: {a.justification}</p>
      )}
      {a.notInPlace > 0 && (
        <p style={{ margin: "0.3rem 0", color: "var(--state-warn)" }}>
          This area contains {a.notInPlace === 1 ? "a practice" : `${a.notInPlace} practices`} answered as not in place.
        </p>
      )}
      {a.status !== "not_applicable" && (
        <table className="report-table" style={{ marginTop: "0.3rem" }}>
          <tbody>
            {a.practices.map((p) => (
              <tr key={p.criterionId}>
                <td>{p.title}</td>
                <td style={{ width: "40%" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                    <HarveyBall
                      share={shareOfScale(p.score, scale)}
                      state={p.response === UNABLE_TO_ASSESS ? "incomplete" : p.response === NOT_APPLICABLE ? "not_applicable" : "share"}
                      size={16}
                      label={p.response}
                    />
                    <span>{p.response}</span>
                  </span>
                  {p.justification ? ` — ${p.justification}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
